import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { launchEnrollment } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, ExternalLink, Stethoscope, Pill, ShieldCheck } from "lucide-react";

const PT_LABEL = { MAPD: "Medicare Advantage (MAPD)", PDP: "Prescription Drug (PDP)", SNP: "Special Needs Plan (SNP)", MEDSUPP: "Medicare Supplement" };

export function IntakeDetailDialog({ open, onOpenChange, intakeId }) {
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && intakeId) {
      setIntake(null);
      setLoading(true);
      api.get(`/medicare/intakes/${intakeId}`).then((r) => setIntake(r.data)).finally(() => setLoading(false));
    }
  }, [open, intakeId]);

  const route = intake?.route;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="intake-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {intake ? `${intake.consumer_first} ${intake.consumer_last}` : "Intake"}
          </DialogTitle>
          <DialogDescription>Saved Medicare intake — captured needs and the routed enrollment destination.</DialogDescription>
        </DialogHeader>
        {loading ? <Skeleton className="h-64 w-full" /> : intake && (
          <div className="space-y-5">
            <Section title="Consumer">
              <Grid rows={[
                ["Date of birth", intake.date_of_birth || "—"],
                ["State", intake.state || "—"],
                ["ZIP", intake.zip_code || "—"],
                ["Product interest", PT_LABEL[intake.product_type] || intake.product_type],
              ]} />
            </Section>

            <Section title="Current coverage">
              <Grid rows={[
                ["Medicare Part A", intake.has_part_a ? "Yes" : "No"],
                ["Medicare Part B", intake.has_part_b ? "Yes" : "No"],
                ["Existing plan", intake.existing_plan || "—"],
                ["Effective date", intake.coverage_effective_date || "—"],
              ]} />
            </Section>

            <Section title="Doctors & medications" icon={Stethoscope}>
              <div className="space-y-3">
                <Chips label="Doctors" items={intake.doctors} icon={Stethoscope} />
                <Chips label="Medications" items={intake.medications} icon={Pill} />
                <p className="text-xs text-muted-foreground">Stored for future permitted prefill — not transmitted to any provider.</p>
              </div>
            </Section>

            <Section title="Enrollment route" icon={ShieldCheck}>
              {route?.resolved ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700 font-semibold text-sm"><CheckCircle2 className="w-4 h-4" /> Routed to {route.provider?.name}</div>
                  <Grid rows={[
                    ["Provider", `${route.provider?.name} (${route.provider?.integration_type})`],
                    ["Account ref", route.profile?.provider_account_ref || "—"],
                    ["Destination", route.destination?.label || "—"],
                  ]} />
                  {route.destination?.url && (
                    <Button size="sm" onClick={() => launchEnrollment(intakeId, route.destination.url)} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="detail-launch">
                      Open destination <ExternalLink className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground text-sm"><XCircle className="w-4 h-4" /> {route?.reason || "Not yet routed."}</div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-mist-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-gold-dark" />}
        <p className="font-display text-base font-bold text-navy-900">{title}</p>
      </div>
      {children}
    </div>
  );
}

function Grid({ rows }) {
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      {rows.map(([k, v]) => (
        <div key={k}><dt className="text-muted-foreground text-xs">{k}</dt><dd className="font-semibold text-navy-900">{v}</dd></div>
      ))}
    </dl>
  );
}

function Chips({ label, items, icon: Icon }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {items?.length ? (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span key={it} className="inline-flex items-center gap-1 text-xs bg-mist-100 text-navy-700 px-2.5 py-1 rounded-full">
              <Icon className="w-3 h-3 text-gold-dark" /> {it}
            </span>
          ))}
        </div>
      ) : <span className="text-sm text-muted-foreground">—</span>}
    </div>
  );
}
